import { MainLayout } from '../../Layout/MainLayout';
import {
  Button,
  ListContentIsland,
  Grid,
  Stack,
  Typography,
  ListItemButton,
  ListItemText,
  ListItem,
  ErrorListItem,
  SkeletonListItem,
  ListItemAvatar,
  AvatarCurrency,
  Link,
} from '@razz-team/ui-web';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useVaultCreateMutation, useVaultProductQuery } from '../../../api/vaults';
import { useIdempotencyKey } from '../../../hooks/useIdempotencyKey';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { ApiProductEarningVaultProductCondition } from '../../../openapi';
import { MainBreadcrumbs } from '../../Layout/MainBreadcrumbs';
import { mapCurrencyToFullName } from '@razz-team/utils';

function VaultConditionListItem({
  condition,
  selected,
  onSelect,
}: {
  condition: ApiProductEarningVaultProductCondition;
  selected: boolean;
  onSelect: () => void;
}) {
  const apy = Number(condition.rate).toFixed(2);

  return (
    <ListItem disablePadding>
      <ListItemButton onClick={onSelect} selected={selected}>
        <ListItemAvatar>
          <AvatarCurrency currency={condition.currency} />
        </ListItemAvatar>
        <ListItemText
          primary={mapCurrencyToFullName(condition.currency)}
          secondary={`${condition.currency} •  ${apy}% APY`}
        />
      </ListItemButton>
    </ListItem>
  );
}

export function CreateVaultPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const idempotencyKey = useIdempotencyKey();
  const vaultProductQuery = useVaultProductQuery();
  const createVaultMutation = useVaultCreateMutation({ idempotencyKey });

  const availableConditions = useMemo(() => {
    const conditions = vaultProductQuery.data?.product?.conditions || [];
    return conditions.filter((condition) => {
      const available = condition.totalLimit - (condition.usage?.number || 0);
      return available > 0;
    });
  }, [vaultProductQuery.data?.product?.conditions]);

  const [selectedProductId, setSelectedProductId] = useState<string | null>(
    availableConditions[0]?.id || null,
  );

  // Set default productId to first available
  useEffect(() => {
    if (!selectedProductId && availableConditions.length > 0) {
      setSelectedProductId(availableConditions[0].id);
    }
  }, [availableConditions, selectedProductId]);

  const handleSubmit = useCallback(() => {
    if (!selectedProductId) return;
    createVaultMutation.mutate({
      productId: selectedProductId,
    });
  }, [createVaultMutation, selectedProductId]);

  useEffect(() => {
    if (createVaultMutation.data) {
      navigate({
        to: '/account/vaults/$vaultId',
        params: { vaultId: createVaultMutation.data.id },
      });
    }
  }, [createVaultMutation.data, navigate]);

  return (
    <MainLayout
      title={
        <MainBreadcrumbs
          title={t('Open vault')}
          parent={{
            to: '/account/vaults',
            title: t('Earning vaults'),
          }}
        />
      }
      compact
    >
      <Grid container size={12}>
        <Stack spacing={4} width="100%">
          <ListContentIsland title={t('Select currency')}>
            {vaultProductQuery.isPending ? (
              <>
                <SkeletonListItem variant="withButton" />
                <SkeletonListItem variant="withButton" />
                <SkeletonListItem variant="withButton" />
              </>
            ) : vaultProductQuery.error ? (
              <ErrorListItem error={vaultProductQuery.error} />
            ) : availableConditions.length === 0 ? (
              <ListItem>
                <Typography variant="body2" color="text.secondary">
                  {t('No vault products available at the moment')}
                </Typography>
              </ListItem>
            ) : (
              availableConditions.map((condition) => (
                <VaultConditionListItem
                  key={condition.id}
                  condition={condition}
                  selected={selectedProductId === condition.id}
                  onSelect={() => setSelectedProductId(condition.id)}
                />
              ))
            )}
          </ListContentIsland>

          {createVaultMutation.error && (
            <Typography variant="body2" color="error">
              {createVaultMutation.error.message}
            </Typography>
          )}

          <Button
            onClick={handleSubmit}
            size="large"
            color="primary"
            loading={createVaultMutation.isPending}
            disabled={!selectedProductId || availableConditions.length === 0}
            fullWidth
          >
            {t('Open vault')}
          </Button>
          <Typography variant="caption" color="text.secondary" align="center">
            <Trans>
              {'By pressing "Open vault", you confirm agreeing to {{productName}}\'s '}
              <Link
                href="https://brighty.app/en/earning-vault-terms-and-conditions"
                target="_blank"
              >
                Terms & Conditions for Earning Vaults
              </Link>
              {'.'}
            </Trans>
          </Typography>
        </Stack>
      </Grid>
    </MainLayout>
  );
}
